import { h, type RefObject } from 'preact';
import * as styles from './transcript-container.scss';
import { timestampToHumanTime } from '../../utils/formatter';
import { withText, Text } from 'preact-i18n';
import type { CuePoint } from '@playkit-js/kaltura-player-js';
import type { Ref } from 'preact/hooks';
import { useCallback, useLayoutEffect, useRef, useState } from 'preact/hooks';
import clsx from 'clsx';

const translations = {
  closePanelButton: <Text id="taxonomyTranscript.seekButtonAriaLabel">Click to seek to this line in the video</Text>
};

interface Props {
  cuePoints: CuePoint[];
  scrollToCuePointId: string | null;
  onTextLineClick: (startTime: number) => void;
  highlightedWord: string;
  /** The cue-point (caption) that is currently being played.  */
  activeCuePoint: CuePoint | null;
  transcriptContainerRef: Ref<HTMLDivElement>;
}
export const TranscriptContainer = withText(translations)(({
  cuePoints,
  scrollToCuePointId,
  onTextLineClick,
  closePanelButton,
  highlightedWord,
  activeCuePoint,
  transcriptContainerRef
}: Props & { closePanelButton: string }) => {
  const includeHours = (cuePoints.slice().reverse().at(0)?.startTime || 0) >= 3600;
  const activeTranscriptLine = useRef<HTMLDivElement>(null);
  const scrollToTranscriptLine = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const getTextComponent = useCallback(
    /**
     * @param text The current cuepoint/line full text.
     * @param isCurrentSearchResultHighlight When `true`, this means that the highlighted word matches the current search result item.
     * For example, if the user searched for the word "hello" and it matches 5 times in the transcript (in separate lines), as the user navigates
     * to different instances of the result (using the up/down arrow buttons), `true` indicates that we're currently on a line that matches that
     * search result position, and we should highlight the word with a slightly different style.
     * @returns
     */
    (text: string, isCurrentSearchResultHighlight: boolean) => {
      if (!highlightedWord) {
        return text;
      }

      if (!text.toLowerCase().includes(highlightedWord.toLowerCase())) {
        return text;
      }

      const escapedHighlight = highlightedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));

      return parts.map((part, index) => {
        if (part.toLowerCase() === highlightedWord.toLowerCase()) {
          return (
            <span key={index} class={isCurrentSearchResultHighlight ? styles.currentMatch : styles.highlight}>
              {part}
            </span>
          );
        }

        return part;
      });
    },
    [highlightedWord]
  );

  useLayoutEffect(() => {
    if (!transcriptContainerRef.current) {
      return;
    }

    setContainerHeight(transcriptContainerRef.current.getClientRects()[0].height ?? 0);
  }, [transcriptContainerRef]);

  const handleAutoScroll = (): void => {
    scrollTo(activeTranscriptLine);
  };

  function handleScrollToSearchResult() {
    scrollTo(scrollToTranscriptLine);
  }

  function scrollTo(element: RefObject<HTMLElement>) {
    if (!transcriptRef.current) {
      return;
    }

    if (!element.current) {
      return;
    }

    transcriptRef.current.scrollTop = element.current.offsetTop - containerHeight / 3;
  }

  return (
    <div ref={transcriptRef} className={styles.container}>
      {cuePoints.map((cuePoint) => {
        const isActiveCuePoint = cuePoint.id === activeCuePoint?.id;
        if (isActiveCuePoint) {
          handleAutoScroll();
        }

        return (
          <div
            key={cuePoint.id}
            ref={(node) => {
              if (!node) {
                return;
              }

              if (isActiveCuePoint) {
                activeTranscriptLine.current = node;
              }

              if (cuePoint.id === scrollToCuePointId) {
                scrollToTranscriptLine.current = node;
                handleScrollToSearchResult();
              }
            }}
            className={styles.transcriptLine}
          >
            <div className={styles.transcriptTimestamp}>{timestampToHumanTime(cuePoint.startTime, includeHours)}</div>
            <button
              aria-label={closePanelButton}
              className={clsx(styles.transcriptText, isActiveCuePoint ? styles.highlightedText : '')}
              onClick={() => {
                onTextLineClick(cuePoint.startTime);
              }}
            >
              {getTextComponent(cuePoint.metadata.text, scrollToCuePointId === cuePoint.id)}
            </button>
          </div>
        );
      })}
    </div>
  );
});
